import { BOOTH_PRODUCT_TYPES, normalizeBoothNumber } from '../../../../utils/boothMap';
import { parsePriceDetails } from './boothMapView';

/**
 * Pure checkout helpers for booth lines (used by src/pages-user/order/checkout/Checkout.jsx and
 * unit-tested next to the hall-plan booking page). Checkout products are the entries of
 * buildCheckoutProducts (src/utils/boothMap.js): one entry per booth with qty 1 and `lineBoothNumber`
 * (the booth chosen on the hall plan), or one entry per product with qty = number of occurrences.
 */

export const ORDER_ERROR_CODES = ['BOOTH_NOT_AVAILABLE', 'BOOTH_NUMBER_REQUIRED', 'INVALID_QTY', 'FORBIDDEN'];

/** Error codes that send the customer back to the booth selection. */
export const BOOTH_SELECTION_ERROR_CODES = ['BOOTH_NOT_AVAILABLE', 'BOOTH_NUMBER_REQUIRED', 'INVALID_QTY'];

export function isBoothTypeProduct(product) {
  return !!(product && BOOTH_PRODUCT_TYPES.includes(product.type));
}

/** 'business' when any product is sold to business customers only, otherwise null (keep the current choice). */
export function deriveCustomerType(products) {
  const list = Array.isArray(products) ? products : [];
  const businessOnly = list.some(
    (product) =>
      product &&
      Array.isArray(product.allowedBuyerGroups) &&
      product.allowedBuyerGroups.length === 1 &&
      product.allowedBuyerGroups[0] === 'business'
  );
  return businessOnly ? 'business' : null;
}

/**
 * order.products[] of placeOrder. Hall-plan booth lines (lineBoothNumber set) are sent as one line per
 * physical booth with qty 1 and `boothNumber`; an entry with qty > 1 is split into qty-1 lines. Entries
 * without a booth number (legacy list booking: pool products with stock n, or a single-booth product whose
 * number placeOrder takes from the product) keep their qty - placeOrder rejects qty > 1 for single-booth
 * products (INVALID_QTY) and accepts it for pool products as before.
 */
export function orderLinesFor(products, taxRate) {
  const lines = [];
  for (const product of Array.isArray(products) ? products : []) {
    if (!product || !product.id) continue;
    if (isBoothTypeProduct(product) && product.lineBoothNumber) {
      const boothNumber = product.lineBoothNumber;
      const count = Math.max(1, Math.floor(Number(product.qty) || 1));
      for (let i = 0; i < count; i += 1) {
        lines.push({ id: product.id, qty: 1, boothNumber, discount: 0, discountType: '', taxRate });
      }
    } else {
      lines.push({ id: product.id, qty: product.qty || 1, discount: 0, discountType: '', taxRate });
    }
  }
  return lines;
}

/** Whether the checkout has hall-plan booth lines (booth=<productId>:<number> params). */
export function hasBoothParams(params) {
  const value = params ? params.booth : undefined;
  const list = Array.isArray(value) ? value : value ? [value] : [];
  return list.some((entry) => typeof entry === 'string' && entry.trim() !== '');
}

/**
 * Checkout entries with the price of THEIR booth from the booth map (getEventBoothMap: booth.price =
 * priceForBooth of the primary offer, booth.priceDetails = its breakdown). Only hall-plan lines
 * (lineBoothNumber) are touched; a line matches the map booth with the same productId and normalized
 * booth number. Without a map, a matching booth or a numeric map price the entry keeps its price
 * (EventProduct price, set by Checkout) and gets priceDetails null. placeOrder sets the binding price.
 *
 * @param {object[]} entries buildCheckoutProducts result
 * @param {object|null} map getEventBoothMap result
 * @returns {object[]} new entries (inputs untouched)
 */
export function applyBoothMapPrices(entries, map) {
  const list = Array.isArray(entries) ? entries : [];
  const byBooth = new Map();
  for (const booth of map && Array.isArray(map.booths) ? map.booths : []) {
    if (!booth || !booth.productId || !booth.boothNumber) continue;
    byBooth.set(`${booth.productId}#${normalizeBoothNumber(booth.boothNumber)}`, booth);
  }
  return list.map((entry) => {
    if (!entry || !entry.lineBoothNumber) return entry;
    const booth = byBooth.get(`${entry.id}#${normalizeBoothNumber(entry.lineBoothNumber)}`);
    const price = booth && booth.price !== null && booth.price !== undefined && booth.price !== '' ? Number(booth.price) : NaN;
    if (!booth || !Number.isFinite(price)) return { ...entry, priceDetails: null };
    return { ...entry, price, priceDetails: parsePriceDetails(booth.priceDetails) };
  });
}

function lineTotal(product) {
  return product.qty && product.qty > 1 ? product.price * product.qty : product.price;
}

/**
 * Net amount of an applied discount code. A fixed amount restricted to products is applied once per
 * product id (several booths of one variant product are several entries of the same product).
 */
export function discountAmountFor(discount, products) {
  const list = (Array.isArray(products) ? products : []).filter(Boolean);
  if (!discount) return 0;
  if (discount.discountType === 'percentage') {
    return list.reduce((acc, product) => {
      if (discount.restrictedProductIds && !discount.restrictedProductIds.includes(product.id)) return acc;
      return acc + (lineTotal(product) * discount.discountPercentage) / 100;
    }, 0);
  }
  if (discount.discountType === 'fixedAmount') {
    if (discount.restrictedProductIds) {
      const ids = new Set(list.map((product) => product.id).filter(Boolean));
      return [...ids].filter((id) => discount.restrictedProductIds.includes(id)).length * discount.discountAmount;
    }
    return discount.discountAmount;
  }
  return 0;
}

/**
 * Parses a placeOrder error message ("BOOTH_NOT_AVAILABLE: N3.1", "BOOTH_NOT_AVAILABLE: N3.1 (duplicate)",
 * "BOOTH_NUMBER_REQUIRED: 18B", "INVALID_QTY: <label>", "FORBIDDEN: not a member ...").
 * Returns { code, label, duplicate, message }; code is null for other errors.
 */
export function parseOrderError(message) {
  const text = String(message || '');
  const match = text.match(/\b(BOOTH_NOT_AVAILABLE|BOOTH_NUMBER_REQUIRED|INVALID_QTY|FORBIDDEN)\b:?[ \t]*([^;\n]*)/);
  if (!match) return { code: null, label: '', duplicate: false, message: text };
  let label = match[2].trim();
  let duplicate = false;
  const duplicateMatch = label.match(/^(.*?)\s*\(duplicate\)$/i);
  if (duplicateMatch) {
    label = duplicateMatch[1].trim();
    duplicate = true;
  }
  return { code: match[1], label, duplicate, message: text };
}

/** Customer-facing text of a parsed order error (literal t() keys for Locize). */
export function orderErrorText(t, error) {
  const code = error ? error.code : null;
  const label = error && error.label ? error.label : '';
  switch (code) {
    case 'BOOTH_NOT_AVAILABLE':
      if (!label) return t('The selected booth is no longer available. Please go back to the hall plan and choose another booth.');
      return error.duplicate
        ? t('Booth {{number}} is included more than once in your order. Please go back to the hall plan and check your selection.', { number: label })
        : t('Booth {{number}} is no longer available. Please go back to the hall plan and choose another booth.', { number: label });
    case 'BOOTH_NUMBER_REQUIRED':
      return t('Please choose a specific booth of variant {{variant}} on the hall plan.', { variant: label || '–' });
    case 'INVALID_QTY':
      return t('Each booth can only be booked once ({{name}}). Please go back to the booth selection and try again.', { name: label || '–' });
    case 'FORBIDDEN':
      return t('You are not allowed to book booths for this company.');
    default:
      return t('Your order could not be placed. Please try again or contact support.');
  }
}
